import { Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';

const PortalPrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  
  // Combined query for session and portal user - single request optimization
  const { data: authData, isLoading, isError } = useQuery({
    queryKey: ['portal-auth'],
    queryFn: async () => {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) throw error;
      if (!session?.user?.id) return { session: null, portalUser: null };
      
      const { data: portalUser, error: userError } = await supabase
        .from('portal_users')
        .select('*')
        .eq('id', session.user.id)
        .maybeSingle();
      
      if (userError && userError.code !== 'PGRST116') throw userError;
      
      return { session, portalUser };
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - more aggressive caching
    refetchOnWindowFocus: false, // Reduce unnecessary refetches
    refetchOnMount: false, // Use cached data on mount
    retry: 1,
  });

  const session = authData?.session;
  const portalUser = authData?.portalUser;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (isError || !session) {
    return <Navigate to="/portal/login" state={{ from: location }} replace />;
  }

  if (!portalUser) {
    if (location.pathname === '/portal/login') {
      return (
        <div className="flex flex-col justify-center items-center h-screen p-4 text-center gap-4">
          <Loader2 className="h-12 w-12 text-muted-foreground animate-spin" />
          <h2 className="text-xl font-semibold">Portal Access Required</h2>
          <p className="text-muted-foreground max-w-md">
            Your account needs portal access configuration. Please contact your administrator.
          </p>
        </div>
      );
    }
    return <Navigate to="/portal/login" state={{ from: location }} replace />;
  }

  if (portalUser.status === 'pending_approval') {
    return <Navigate to="/portal/pending-approval" state={{ from: location }} replace />;
  }

  if (portalUser.status === 'suspended') {
    return <Navigate to="/portal/suspended" state={{ from: location }} replace />;
  }

  if (portalUser.status !== 'approved') {
    return <Navigate to="/portal/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export default PortalPrivateRoute;